/**
 * Pure rules of the axis-page prompt matrix (lot F step 8d, UI-SPEC §2.30-§2.31),
 * kept out of `BrandAxisPage.tsx` so they are unit-tested.
 *
 *  - A fact without `s` is a point with NO value (« Non évalué »), never a 0.
 *  - A prompt's series breaks where its `scoreSeriesKey` changes, and only there: the
 *    first measured cell of a new kind is marked (`kindBreaks`).
 *  - A month column (`col.month`, `day` = the month's first day) is labelled as a month.
 */

import { scoreSeriesKey, type BPPromptFact, type BPPromptMatrixColumn } from '@traaker/shared';

export const isScored = (cell: BPPromptFact | null | undefined): cell is BPPromptFact & { s: number } =>
  !!cell && typeof cell.s === 'number' && Number.isFinite(cell.s);

/**
 * Index-aligned to `cells`: `true` on a measured cell whose series key differs from the
 * row's PREVIOUS measured cell. Unmeasured / unscored cells are never marked and never
 * reset the comparison.
 */
export function kindBreaks(cells: (BPPromptFact | null)[]): boolean[] {
  let prev: string | null = null;
  return cells.map((cell) => {
    if (!isScored(cell)) return false;
    const key = scoreSeriesKey(cell);
    const changed = prev !== null && key !== prev;
    prev = key;
    return changed;
  });
}

/** The newest SCORED value of a row, `null` when none (an unscored fact is skipped). */
export function lastScore(cells: (BPPromptFact | null)[]): number | null {
  for (let i = cells.length - 1; i >= 0; i -= 1) {
    const cell = cells[i];
    if (isScored(cell)) return cell.s;
  }
  return null;
}

/** Header label of a column: « oct. 26 » for a month column, « 03 oct. » for a day. */
export function columnLabel(col: Pick<BPPromptMatrixColumn, 'day' | 'month'>, lang: string): string {
  if (col.month || col.day.length === 7) {
    // The FULL year: « nov. 26 » read as the 26th of November (dev, 2026-11-02).
    return new Date(`${col.day.slice(0, 7)}-01T00:00:00.000Z`).toLocaleDateString(lang, {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  return new Date(`${col.day}T00:00:00.000Z`).toLocaleDateString(lang, {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * The audit(s) a drill-down reads for one cell. The cell's own `ae` (per engine) or
 * `aid` wins: a month column has no `auditId`, and a new-pipeline day's audit holds
 * only the runs it launched (00b DQ5). Legacy facts carry neither → the column's audit.
 * `engine` set = only that engine's row is read from that audit.
 */
export function cellSources(
  col: Pick<BPPromptMatrixColumn, 'auditId'> | undefined,
  cell: BPPromptFact | null | undefined,
): { auditId: string; engine?: string }[] {
  if (cell?.ae && Object.keys(cell.ae).length > 0) {
    return Object.entries(cell.ae).map(([engine, auditId]) => ({ auditId, engine }));
  }
  if (cell?.aid) return [{ auditId: cell.aid }];
  return col?.auditId ? [{ auditId: col.auditId }] : [];
}
